import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreatePrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type Props = {
    open: boolean;
    onClose: () => void;
};

export const NewSkillModal: FC<Props> = ({ open, onClose }) => {
    const [name, setName] = useState("");
    const [trigger, setTrigger] = useState("");
    const [description, setDescription] = useState("");
    const [prompt, setPrompt] = useState("");
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const createPrompt = useCreatePrompt();
    const { toast } = useToast();

    const markTouched = (field: string) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const handleSave = useCallback(async () => {
        try {
            await createPrompt.mutateAsync({
                name: name.trim(),
                description: description.trim(),
                prompt: prompt.trim(),
                isCommand: true, // Keep for backward compatibility
                isSkill: true, // Mark as skill
                command: trigger.trim(),
            });
            toast({
                title: "Skill created successfully",
                description: `/${trigger} is now available to your organization`,
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to create skill",
                description: formatError(error),
            });
        }
    }, [name, trigger, description, prompt, createPrompt, toast, onClose]);

    const isValid = name.trim() && trigger.trim() && description.trim() && prompt.trim();

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="new-skill-modal"
                data-track-location={TrackLocations.NewSkillModal}
            >
                <DialogHeader>
                    <DialogTitle>New Agent Skill</DialogTitle>
                    <DialogDescription>Create a custom skill for your organization</DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <InputField
                        id="skill-name"
                        label="Name"
                        error={touched.name && !name.trim() ? "Name is required" : undefined}
                    >
                        <Input
                            id="skill-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => markTouched("name")}
                            placeholder="Skill name"
                            data-testid="skill-name-input"
                        />
                    </InputField>

                    <InputField
                        id="skill-trigger"
                        label="Slash Trigger"
                        hint="Type this in the chat to invoke the skill directly"
                        error={touched.trigger && !trigger.trim() ? "Trigger is required" : undefined}
                    >
                        <Input
                            id="skill-trigger"
                            value={trigger}
                            onChange={(e) => setTrigger(e.target.value)}
                            onBlur={() => markTouched("trigger")}
                            placeholder="trigger-word"
                            prefix="/"
                            data-testid="skill-trigger-input"
                        />
                    </InputField>

                    <InputField
                        id="skill-description"
                        label="Description"
                        error={touched.description && !description.trim() ? "Description is required" : undefined}
                    >
                        <Input
                            id="skill-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            onBlur={() => markTouched("description")}
                            placeholder="Brief description of what this skill does"
                            data-testid="skill-description-input"
                        />
                    </InputField>

                    <InputField
                        id="skill-prompt"
                        label="Prompt"
                        hint={`${prompt.length.toLocaleString()} / ${(20000).toLocaleString()} characters`}
                        error={touched.prompt && !prompt.trim() ? "Prompt is required" : undefined}
                    >
                        <Textarea
                            id="skill-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onBlur={() => markTouched("prompt")}
                            placeholder="The prompt that will be sent to the AI agent"
                            minHeight={120}
                            maxHeight={300}
                            className="resize-none"
                            data-testid="skill-prompt-textarea"
                            maxLength={20000}
                        />
                    </InputField>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid}
                        loading={createPrompt.isPending}
                        data-testid="save-skill-button"
                        data-tracking-id="create-agent-skill-new-skill-modal"
                    >
                        Create Skill
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
